import React, { useState, useEffect } from "react";
import PageHeader from "../components/shared/PageHeader";
import PageContainer from "../components/shared/PageContainer";
import {
  Alert,
  Badge,
  Button,
  Drawer,
  Select,
  Spinner,
  TextInput,
  Td,
  Th,
  Tr,
} from "../components/ui";
import { userApi, entityApi } from "../services/api";

interface User {
  id: number;
  username: string;
  fullname: string;
  modules: string;
  preferences?: string;
  createdAt: string;
  expireAt: string;
  supplierId?: number | null;
  clientId?: number | null;
  supplierName?: string | null;
  clientName?: string | null;
}

interface EntityOption {
  id: number;
  businessName: string;
}

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [suppliers, setSuppliers] = useState<EntityOption[]>([]);
  const [clients, setClients] = useState<EntityOption[]>([]);

  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [showViewOffcanvas, setShowViewOffcanvas] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [viewingUser, setViewingUser] = useState<User | null>(null);

  // Form State
  const [formUsername, setFormUsername] = useState("");
  const [formPassword, setFormPassword] = useState("");
  const [formFullname, setFormFullname] = useState("");
  const [formModules, setFormModules] = useState("1");
  const [formSupplierId, setFormSupplierId] = useState<string>("");
  const [formClientId, setFormClientId] = useState<string>("");

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await userApi.getAllUsers();
      setUsers(data);
    } catch (err: any) {
      setError("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const fetchEntities = async () => {
    try {
      const [supplierData, clientData] = await Promise.all([
        entityApi.getAllSuppliers(),
        entityApi.getAllClients(),
      ]);
      setSuppliers(supplierData);
      setClients(clientData);
    } catch (err) {
      // Silently fail — dropdowns will just be empty
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchEntities();
  }, []);

  const resetForm = () => {
    setEditingUser(null);
    setFormUsername("");
    setFormPassword("");
    setFormFullname("");
    setFormModules("1");
    setFormSupplierId("");
    setFormClientId("");
  };

  const handleOpenAdd = () => {
    resetForm();
    setShowOffcanvas(true);
  };

  const handleOpenEdit = (user: User) => {
    resetForm();
    setEditingUser(user);
    setFormUsername(user.username);
    setFormFullname(user.fullname);
    setFormModules(user.modules);
    setFormSupplierId(user.supplierId ? String(user.supplierId) : "");
    setFormClientId(user.clientId ? String(user.clientId) : "");
    setShowOffcanvas(true);
  };

  const handleOpenView = (user: User) => {
    setViewingUser(user);
    setShowViewOffcanvas(true);
  };

  const handleClose = () => {
    setShowOffcanvas(false);
  };

  const handleCloseView = () => {
    setShowViewOffcanvas(false);
    setViewingUser(null);
  };

  const handleDelete = async (user: User) => {
    if (!window.confirm(`Are you sure you want to delete "${user.fullname}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await userApi.deleteUser({ id: user.id });
      fetchUsers();
    } catch (err) {
      alert("Failed to delete user.");
    }
  };

  const handleAccessChange = (value: string) => {
    setFormModules(value);
    // Reset entity selections when access type changes
    if (value !== "3") setFormSupplierId("");
    if (value !== "2") setFormClientId("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formUsername || !formFullname || !formModules || (!editingUser && !formPassword)) {
      alert("Please fill in all required fields.");
      return;
    }

    if (formModules === "3" && !formSupplierId) {
      alert("Please select a supplier.");
      return;
    }

    if (formModules === "2" && !formClientId) {
      alert("Please select a client.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        id: editingUser?.id || 0,
        username: formUsername,
        password: formPassword || "",
        fullname: formFullname,
        modules: formModules,
        supplierId: formModules === "3" ? Number(formSupplierId) : null,
        clientId: formModules === "2" ? Number(formClientId) : null,
      };

      if (editingUser) {
        await userApi.updateUser(payload);
      } else {
        await userApi.addUser(payload);
      }

      setShowOffcanvas(false);
      fetchUsers();
    } catch (err) {
      alert("Failed to save user.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderAccessBadge = (user: User) => {
    const mod = user.modules?.trim();

    if (mod === "1") {
      return <Badge tone="danger">Admin</Badge>;
    }
    if (mod === "3" && user.supplierName) {
      return (
        <span className="inline-flex items-center gap-1.5">
          <Badge tone="brand">Supplier</Badge>
          <i className="bi bi-chevron-right text-[10px] text-zinc-300" />
          <span className="font-medium text-zinc-900">{user.supplierName}</span>
        </span>
      );
    }
    if (mod === "2" && user.clientName) {
      return (
        <span className="inline-flex items-center gap-1.5">
          <Badge tone="success">Branch</Badge>
          <i className="bi bi-chevron-right text-[10px] text-zinc-300" />
          <span className="font-medium text-zinc-900">{user.clientName}</span>
        </span>
      );
    }

    // Fallback
    if (mod === "3") return <Badge tone="brand">Supplier</Badge>;
    if (mod === "2") return <Badge tone="success">Branch</Badge>;
    return <Badge>{mod || "None"}</Badge>;
  };

  const formId = "user-form";

  return (
    <PageContainer>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <PageHeader title="Manage Users" subTitle="Users" />
        <Button variant="primary" onClick={handleOpenAdd} className="shrink-0">
          <i className="bi bi-person-plus-fill" />
          Create User
        </Button>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}

      <section className="panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <Th>Full Name</Th>
                <Th>Username</Th>
                <Th>Access</Th>
                <Th>Created</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center">
                    <Spinner className="mx-auto" />
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-12 text-center text-sm text-zinc-500"
                  >
                    No users found.
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <Tr key={user.id}>
                    <Td className="font-medium text-zinc-900">
                      {user.fullname}
                    </Td>
                    <Td className="text-zinc-500">{user.username}</Td>
                    <Td>{renderAccessBadge(user)}</Td>
                    <Td className="text-zinc-500">
                      {new Date(user.createdAt).toLocaleDateString("en-GB")}
                    </Td>
                    <Td className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          variant="ghost"
                          onClick={() => handleOpenView(user)}
                          className="px-3 py-1.5 text-xs"
                        >
                          <i className="bi bi-eye" />
                          View
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleOpenEdit(user)}
                          className="px-3 py-1.5 text-xs"
                        >
                          <i className="bi bi-pencil" />
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => handleDelete(user)}
                          className="px-3 py-1.5 text-xs"
                        >
                          <i className="bi bi-trash" />
                          Delete
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Side panel for Create/Edit User */}
      <Drawer
        open={showOffcanvas}
        onOpenChange={(open) => !open && handleClose()}
        title={editingUser ? "Edit User" : "Create New User"}
        footer={
          <>
            <Button variant="outline" onClick={handleClose} className="flex-1">
              Cancel
            </Button>
            <Button
              type="submit"
              form={formId}
              variant="primary"
              disabled={submitting}
              className="flex-1"
            >
              {submitting ? (
                <>
                  <Spinner size="size-4" />
                  Saving…
                </>
              ) : editingUser ? (
                "Update User"
              ) : (
                "Create User"
              )}
            </Button>
          </>
        }
      >
        <form id={formId} onSubmit={handleSubmit} className="space-y-4">
          <TextInput
            label="Full Name *"
            type="text"
            placeholder="e.g. John Doe"
            value={formFullname}
            onChange={(e) => setFormFullname(e.target.value)}
            required
          />

          <TextInput
            label="Username *"
            type="text"
            placeholder="e.g. johndoe"
            value={formUsername}
            onChange={(e) => setFormUsername(e.target.value)}
            required
          />

          <TextInput
            label={
              editingUser ? "Password (blank keeps current)" : "Password *"
            }
            type="password"
            placeholder="Enter password"
            value={formPassword}
            onChange={(e) => setFormPassword(e.target.value)}
            required={!editingUser}
          />

          <Select
            label="Access *"
            value={formModules}
            onChange={(e) => handleAccessChange(e.target.value)}
            required
          >
            <option value="1">Admin</option>
            <option value="3">Supplier</option>
            <option value="2">Branch</option>
          </Select>

          {formModules === "3" && (
            <Select
              label="Supplier *"
              value={formSupplierId}
              onChange={(e) => setFormSupplierId(e.target.value)}
              required
            >
              <option value="">Select a supplier…</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.businessName}
                </option>
              ))}
            </Select>
          )}

          {formModules === "2" && (
            <Select
              label="Branch *"
              value={formClientId}
              onChange={(e) => setFormClientId(e.target.value)}
              required
            >
              <option value="">Select a branch…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.businessName}
                </option>
              ))}
            </Select>
          )}
        </form>
      </Drawer>

      {/* Side panel for User details */}
      <Drawer
        open={showViewOffcanvas}
        onOpenChange={(open) => !open && handleCloseView()}
        title="User Details"
        footer={
          <>
            <Button variant="outline" onClick={handleCloseView} className="flex-1">
              Close
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={() => {
                const user = viewingUser;
                handleCloseView();
                if (user) handleOpenEdit(user);
              }}
            >
              <i className="bi bi-pencil" />
              Edit
            </Button>
          </>
        }
      >
        {viewingUser && (
          <dl className="space-y-5">
            {[
              { label: "Full Name", value: viewingUser.fullname },
              { label: "Username", value: viewingUser.username },
              { label: "Access", value: renderAccessBadge(viewingUser) },
              {
                label: "Created At",
                value: new Date(viewingUser.createdAt).toLocaleDateString(
                  "en-GB",
                  { day: "numeric", month: "long", year: "numeric" }
                ),
              },
              {
                label: "Expires At",
                value: new Date(viewingUser.expireAt).toLocaleDateString(
                  "en-GB",
                  { day: "numeric", month: "long", year: "numeric" }
                ),
              },
            ].map(({ label, value }) => (
              <div key={label}>
                <dt className="text-xs font-medium tracking-wide text-zinc-500 uppercase">
                  {label}
                </dt>
                <dd className="mt-1 text-sm text-zinc-900">{value}</dd>
              </div>
            ))}
          </dl>
        )}
      </Drawer>
    </PageContainer>
  );
};

export default Users;
